import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Package, ShoppingCart, Users, AlertTriangle, FileCheck, Clock, 
  DollarSign, TrendingUp, Bell, ArrowRight, Zap
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#78BE20', '#3B82F6', '#A855F7', '#22C55E', '#EF4444'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setStats(res.data.stats);
      setRecentOrders(res.data.recent_orders);
      setCharts(res.data.charts);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'processing': return 'bg-blue-500/20 text-blue-500';
      case 'shipped': return 'bg-purple-500/20 text-purple-500';
      case 'delivered': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'cancelled': return 'bg-red-500/20 text-red-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const hasPendingOrders = stats?.pending_orders > 0 || stats?.processing_orders > 0;

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard" className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">
            Dashboard
          </h1>
          <div className="text-sm text-zinc-400">
            {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Pending Orders Alert Banner */}
        {hasPendingOrders && !loading && (
          <Link to="/admin/orders?status=pending">
            <div className="relative overflow-hidden bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border border-yellow-500/50 rounded-lg p-4 animate-pulse-slow">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-transparent animate-shimmer" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-yellow-500 rounded-full animate-ping opacity-25" />
                    <div className="relative bg-yellow-500 p-3 rounded-full">
                      <Bell className="h-6 w-6 text-black" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      Orders Require Attention!
                    </h3>
                    <p className="text-yellow-200/80">
                      {stats?.pending_orders > 0 && <span className="font-bold">{stats.pending_orders} pending</span>}
                      {stats?.pending_orders > 0 && stats?.processing_orders > 0 && ' and '}
                      {stats?.processing_orders > 0 && <span className="font-bold">{stats.processing_orders} processing</span>}
                      {' '}orders waiting for action
                    </p>
                  </div>
                </div>
                <Button className="bg-yellow-500 text-black hover:bg-yellow-400 font-bold">
                  View Orders <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </Link>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Revenue */}
          <Card className="bg-gradient-to-br from-[#78BE20]/20 to-[#78BE20]/5 border-[#78BE20]/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Total Revenue</p>
                  <p className="text-3xl font-black text-white font-mono mt-1">
                    ${loading ? '---' : (stats?.total_revenue || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-[#78BE20]/20 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-[#78BE20]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Orders */}
          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Total Orders</p>
                  <p className="text-3xl font-black text-white font-mono mt-1">
                    {loading ? '-' : stats?.total_orders || 0}
                  </p>
                </div>
                <div className="bg-blue-500/20 p-3 rounded-full">
                  <ShoppingCart className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products */}
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Products</p>
                  <p className="text-3xl font-black text-white font-mono mt-1">
                    {loading ? '-' : stats?.total_products || 0}
                  </p>
                </div>
                <div className="bg-purple-500/20 p-3 rounded-full">
                  <Package className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users */}
          <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border-cyan-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Total Users</p>
                  <p className="text-3xl font-black text-white font-mono mt-1">
                    {loading ? '-' : stats?.total_users || 0}
                  </p>
                </div>
                <div className="bg-cyan-500/20 p-3 rounded-full">
                  <Users className="h-6 w-6 text-cyan-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/orders?status=pending">
            <Card className={`bg-[#18181b] border-zinc-800 hover:border-yellow-500/50 transition-all ${stats?.pending_orders > 0 ? 'ring-2 ring-yellow-500/30' : ''}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{loading ? '-' : stats?.pending_orders || 0}</p>
                    <p className="text-xs text-zinc-500">Pending Orders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/inventory">
            <Card className={`bg-[#18181b] border-zinc-800 hover:border-red-500/50 transition-all ${stats?.low_stock > 0 ? 'ring-2 ring-red-500/30' : ''}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{loading ? '-' : stats?.low_stock || 0}</p>
                    <p className="text-xs text-zinc-500">Low Stock</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/trade-applications">
            <Card className={`bg-[#18181b] border-zinc-800 hover:border-purple-500/50 transition-all ${stats?.pending_applications > 0 ? 'ring-2 ring-purple-500/30' : ''}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{loading ? '-' : stats?.pending_applications || 0}</p>
                    <p className="text-xs text-zinc-500">Trade Apps</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/orders?status=processing">
            <Card className="bg-[#18181b] border-zinc-800 hover:border-blue-500/50 transition-all">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-white">{loading ? '-' : stats?.processing_orders || 0}</p>
                    <p className="text-xs text-zinc-500">Processing</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sales Chart - Takes 2 columns */}
          <Card className="bg-[#18181b] border-zinc-800 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#78BE20]" />
                Sales Last 7 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 bg-zinc-800 rounded animate-pulse" />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={charts?.sales_by_day || []}>
                    <defs>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#78BE20" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#78BE20" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value, name) => [name === 'sales' ? `$${value}` : value, name === 'sales' ? 'Revenue' : 'Orders']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#78BE20" 
                      strokeWidth={3}
                      fill="url(#salesGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Order Status Pie Chart */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Orders by Status</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-48 bg-zinc-800 rounded animate-pulse" />
              ) : charts?.orders_by_status?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={charts.orders_by_status}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {charts.orders_by_status.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-zinc-500">
                  No order data yet
                </div>
              )}
              {charts?.orders_by_status && (
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {charts.orders_by_status.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-1 text-xs">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-zinc-400 capitalize">{entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Recent Orders</CardTitle>
              <Link to="/admin/orders">
                <Button variant="ghost" size="sm" className="text-[#78BE20] hover:text-[#78BE20]/80">
                  View All <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-14 bg-zinc-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : recentOrders.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">No orders yet</p>
              ) : (
                <div className="space-y-3">
                  {recentOrders.map(order => (
                    <Link
                      key={order.id}
                      to="/admin/orders"
                      className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${order.status === 'pending' ? 'bg-yellow-500 animate-pulse' : order.status === 'processing' ? 'bg-blue-500' : 'bg-[#78BE20]'}`} />
                        <div>
                          <p className="text-white font-medium text-sm">
                            #{order.id.slice(0, 8).toUpperCase()}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(order.status)}>
                          {order.status}
                        </Badge>
                        <span className="text-[#78BE20] font-mono font-bold text-sm">
                          ${order.total.toFixed(2)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low Stock Products */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Low Stock Alert
              </CardTitle>
              <Link to="/admin/inventory">
                <Button variant="ghost" size="sm" className="text-[#78BE20] hover:text-[#78BE20]/80">
                  View Inventory <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-10 bg-zinc-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : charts?.low_stock_products?.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">All stock levels healthy</p>
              ) : (
                <div className="space-y-2">
                  {charts?.low_stock_products?.map((product, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-zinc-900 rounded">
                      <span className="text-sm text-white truncate flex-1 mr-2">{product.name}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-mono font-bold ${product.stock_qty <= 5 ? 'text-red-500' : 'text-yellow-500'}`}>
                          {product.stock_qty} left
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </AdminLayout>
  );
}
